import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { TextField, type TextFieldProps } from "../TextField";
import { DropdownMenu, type DropdownMenuItem } from "../Dropdown/DropdownMenu";
import { BottomSheet } from "../BottomSheet";

export interface SearchableDropdownItem extends DropdownMenuItem {
  [key: string]: any;
}

export interface SearchableDropdownProps
  extends Omit<TextFieldProps, "onChange" | "value"> {
  /**
   * Items to display in the dropdown
   */
  items?: SearchableDropdownItem[];
  /**
   * Section heading text
   */
  sectionHeading?: string;
  /**
   * Whether the dropdown is in loading state
   */
  isLoading?: boolean;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Empty state link text
   */
  emptyLinkText?: string;
  /**
   * Empty state link click handler
   */
  onEmptyLinkClick?: () => void;
  /**
   * Primary button text
   */
  primaryButtonText?: string;
  /**
   * Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Primary button click handler
   */
  onPrimaryClick?: () => void;
  /**
   * Secondary button click handler
   */
  onSecondaryClick?: () => void;
  /**
   * Dropdown menu width
   */
  dropdownWidth?: "auto" | "full";
  /**
   * Show chevron on list items
   */
  showChevron?: boolean;
  /**
   * Custom empty state icon
   */
  emptyIcon?: React.ReactNode;
  /**
   * Disable footer buttons
   */
  disableFooter?: boolean;
  /**
   * Footer layout orientation
   */
  footerLayout?: "horizontal" | "vertical";
  /**
   * Callback when search value changes
   */
  onSearchChange?: (value: string) => void;
  /**
   * Callback when item is selected
   */
  onItemSelect?: (item: SearchableDropdownItem) => void;
  /**
   * Custom filter function
   */
  filterFunction?: (item: SearchableDropdownItem, query: string) => boolean;
  /**
   * Search value (controlled)
   */
  searchValue?: string;
  /**
   * Default search value (uncontrolled)
   */
  defaultSearchValue?: string;
  /**
   * Custom class for dropdown menu
   */
  dropdownClassName?: string;
  /**
   * Minimum characters before showing dropdown
   */
  minSearchLength?: number;
  /**
   * Show dropdown on focus
   */
  showOnFocus?: boolean;
  /**
   * Show "Add New" option when search doesn't match any items
   */
  showAddNew?: boolean;
  /**
   * If true, "Add New" option will only appear when no items match.
   * If false, "Add New" option will always appear at the top of the list.
   * @default true
   */
  showAddNewIfDoesNotMatch?: boolean;
  /**
   * Label for the "Add New" option (e.g., "Client", "User", "Company")
   * Used when showAddNew is true and showAddNewIfDoesNotMatch is false
   */
  addNewLabel?: string;
  /**
   * Callback when "Add New" option is clicked
   * @param value - The search text that the user typed
   */
  onAddNew?: (value: string) => void;
  /**
   * Selected value (controlled)
   * When provided, finds the matching item and pre-populates the search field with its label
   */
  value?: string | number;
  /**
   * Default selected value (uncontrolled)
   * When provided, finds the matching item and pre-populates the search field with its label
   */
  defaultValue?: string | number;
  /**
   * Callback when value changes
   */
  onChange?: (
    value: string | number | undefined,
    item: SearchableDropdownItem | undefined
  ) => void;
}

const defaultFilter = (
  item: SearchableDropdownItem,
  query: string
): boolean => {
  const searchQuery = query?.toLowerCase();
  return (
    item.label?.toLowerCase()?.includes(searchQuery) ||
    (item.description?.toLowerCase()?.includes(searchQuery) ?? false)
  );
};

export const SearchableDropdown = React.forwardRef<
  HTMLInputElement,
  SearchableDropdownProps
>(
  (
    {
      className,
      items = [],
      sectionHeading,
      isLoading = false,
      emptyTitle = "No Search Results Found",
      emptyDescription = "Add description of what the user can search for here.",
      emptyLinkText = "Link to support site",
      onEmptyLinkClick,
      primaryButtonText,
      secondaryButtonText,
      onPrimaryClick,
      onSecondaryClick,
      dropdownWidth = "full",
      showChevron = false,
      emptyIcon,
      disableFooter = false,
      footerLayout = "horizontal",
      onSearchChange,
      onItemSelect,
      filterFunction = defaultFilter,
      searchValue: controlledSearchValue,
      defaultSearchValue = "",
      dropdownClassName,
      minSearchLength = 0,
      showOnFocus = true,
      showAddNew = false,
      showAddNewIfDoesNotMatch = true,
      addNewLabel = "Item",
      onAddNew,
      containerClassName,
      value: controlledValue,
      defaultValue,
      onChange,
      ...textFieldProps
    },
    ref
  ) => {
    // Find the selected item based on value/defaultValue
    const findSelectedItem = React.useCallback(
      (
        val: string | number | undefined
      ): SearchableDropdownItem | undefined => {
        if (val === undefined || val === "") return undefined;
        return items.find((item) => item.value === val);
      },
      [items]
    );

    // Initialize uncontrolled value state
    const initialValue =
      controlledValue !== undefined ? controlledValue : defaultValue;
    const initialSelectedItem = findSelectedItem(initialValue);
    const initialSearchValue = initialSelectedItem
      ? initialSelectedItem.label
      : defaultSearchValue;

    const [uncontrolledSearchValue, setUncontrolledSearchValue] =
      React.useState(initialSearchValue);
    const [uncontrolledValue, setUncontrolledValue] = React.useState<
      string | number | undefined
    >(defaultValue);
    const [isOpen, setIsOpen] = React.useState(false);
    const [focusedIndex, setFocusedIndex] = React.useState(-1);
    const [isInsideModal, setIsInsideModal] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const VIEWPORT_PADDING = 8;
    const MENU_MAX_HEIGHT = 400;
    const MIN_SPACE_TO_OPEN_BELOW = 200;

    const [position, setPosition] = React.useState<{
      top?: number;
      bottom?: number;
      left: number;
      width: number;
      maxHeight: number;
    }>({
      left: 0,
      width: 200,
      maxHeight: MENU_MAX_HEIGHT,
    });

    // Detect if we're on mobile (< 768px)
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      // Check on mount
      checkMobile();

      // Add resize listener
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    React.useImperativeHandle(ref, () => inputRef.current!);

    // Determine current value (controlled or uncontrolled)
    const value =
      controlledValue !== undefined ? controlledValue : uncontrolledValue;

    // Sync search value when value prop changes
    React.useEffect(() => {
      const selectedItem = findSelectedItem(value);
      if (selectedItem) {
        const newSearchValue = selectedItem.label;
        if (controlledSearchValue === undefined) {
          setUncontrolledSearchValue(newSearchValue);
        }
        // If controlled, we still need to call onSearchChange to notify parent
        // but only if the search value is different
        if (
          controlledSearchValue !== undefined &&
          controlledSearchValue !== newSearchValue
        ) {
          onSearchChange?.(newSearchValue);
        }
      } else if (value === undefined || value === "") {
        // If value is cleared, clear search value too
        if (controlledSearchValue === undefined) {
          setUncontrolledSearchValue("");
        } else {
          onSearchChange?.("");
        }
      }
    }, [value, findSelectedItem, controlledSearchValue, onSearchChange]);

    // Check if dropdown is inside a modal
    React.useEffect(() => {
      if (isOpen && dropdownRef.current) {
        let element: HTMLElement | null = dropdownRef.current;
        let foundModal = false;
        while (element && !foundModal) {
          const styles = window.getComputedStyle(element);
          const zIndex = parseInt(styles.zIndex, 10);
          // Check if element has modal z-index (10000) or is a modal container
          if (zIndex === 10000 || element.getAttribute("role") === "dialog") {
            foundModal = true;
            setIsInsideModal(true);
            break;
          }
          element = element.parentElement;
        }
        if (!foundModal) {
          setIsInsideModal(false);
        }
      }
    }, [isOpen]);

    // Update position when dropdown opens or window resizes - viewport coords, open above if not enough space below
    const updateMenuPosition = React.useCallback(() => {
      if (!dropdownRef.current) return;
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
      const spaceAbove = rect.top - VIEWPORT_PADDING;

      const openAbove =
        spaceBelow < MIN_SPACE_TO_OPEN_BELOW || spaceBelow < spaceAbove;

      const menuLeft = Math.max(
        VIEWPORT_PADDING,
        Math.min(rect.left, viewportWidth - Math.max(rect.width, 200) - VIEWPORT_PADDING)
      );
      const menuWidth = Math.max(rect.width, 200);

      if (openAbove) {
        setPosition({
          bottom: viewportHeight - rect.top + VIEWPORT_PADDING,
          left: menuLeft,
          width: menuWidth,
          maxHeight: Math.max(120, Math.min(MENU_MAX_HEIGHT, spaceAbove)),
        });
      } else {
        setPosition({
          top: rect.bottom + VIEWPORT_PADDING,
          left: menuLeft,
          width: menuWidth,
          maxHeight: Math.max(120, Math.min(MENU_MAX_HEIGHT, spaceBelow)),
        });
      }
    }, []);

    React.useLayoutEffect(() => {
      if (isOpen) {
        updateMenuPosition();
      }
    }, [isOpen, updateMenuPosition]);

    React.useEffect(() => {
      if (!isOpen) return;
      window.addEventListener("resize", updateMenuPosition);
      window.addEventListener("scroll", updateMenuPosition, true);
      return () => {
        window.removeEventListener("resize", updateMenuPosition);
        window.removeEventListener("scroll", updateMenuPosition, true);
      };
    }, [isOpen, updateMenuPosition]);

    const searchValue =
      controlledSearchValue !== undefined
        ? controlledSearchValue
        : uncontrolledSearchValue;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (controlledSearchValue === undefined) {
        setUncontrolledSearchValue(newValue);
      }
      onSearchChange?.(newValue);

      // If user is typing and the search value no longer matches the selected item's label,
      // clear the value to indicate no item is selected
      const selectedItem = findSelectedItem(value);
      if (selectedItem && selectedItem.label !== newValue) {
        if (controlledValue === undefined) {
          setUncontrolledValue(undefined);
        }
        onChange?.(undefined, undefined);
      }

      // Show dropdown if minimum search length is met
      if (newValue.length >= minSearchLength) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    const handleFocus = () => {
      if (showOnFocus && searchValue.length >= minSearchLength) {
        setIsOpen(true);
      }
    };

    const handleItemSelect = (item: SearchableDropdownItem) => {
      onItemSelect?.(item);

      // Update search value
      if (controlledSearchValue === undefined) {
        setUncontrolledSearchValue(item.label);
      } else {
        onSearchChange?.(item.label);
      }

      // Update value (controlled or uncontrolled)
      if (controlledValue === undefined) {
        setUncontrolledValue(item.value);
      }

      // Call onChange callback
      onChange?.(item.value, item);

      setIsOpen(false);
      inputRef.current?.focus();
    };

    // Filter items based on search
    const filteredItems = React.useMemo(() => {
      if (!searchValue) return items;
      return items.filter((item) => filterFunction(item, searchValue));
    }, [items, searchValue, filterFunction]);

    // Add "Add New" option based on showAddNew and showAddNewIfDoesNotMatch props
    const itemsWithAddNew = React.useMemo(() => {
      if (!showAddNew) {
        return filteredItems;
      }

      // When showAddNewIfDoesNotMatch is false, show "Add New" even without search value
      const shouldShowAddNew = showAddNewIfDoesNotMatch
        ? searchValue.length > 0
        : true;

      if (!shouldShowAddNew) {
        return filteredItems;
      }

      // Determine the label for "Add New" option
      const addNewDisplayLabel = searchValue
        ? `+ Add "${searchValue}"`
        : `+ Add New ${addNewLabel}`;

      const addNewItem: SearchableDropdownItem = {
        value: searchValue || `new-${addNewLabel}`,
        label: addNewDisplayLabel,
        variant: "primary",
        onClick: () => {
          if (onAddNew) {
            onAddNew(searchValue);
            // Don't update search value here - let parent handle it via onAddNew
            setIsOpen(false);
            inputRef.current?.focus();
          } else {
            // When onAddNew is not provided, create a new item and select it
            const newItem: SearchableDropdownItem = {
              value: searchValue,
              label: searchValue,
            };
            // Use handleItemSelect to ensure proper behavior
            handleItemSelect(newItem);
          }
        },
      };

      // If showAddNewIfDoesNotMatch is true, only show "Add New" when no items match
      // If false, always show "Add New" at the top
      if (showAddNewIfDoesNotMatch) {
        if (filteredItems.length > 0) {
          return filteredItems;
        }
        return [addNewItem];
      } else {
        // Always show "Add New" at the top
        return [addNewItem, ...filteredItems];
      }
    }, [
      showAddNew,
      showAddNewIfDoesNotMatch,
      addNewLabel,
      searchValue,
      filteredItems,
      onItemSelect,
      onAddNew,
      controlledSearchValue,
    ]);

    // Reset focused index when items change
    React.useEffect(() => {
      setFocusedIndex(-1);
    }, [itemsWithAddNew.length]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "Enter") {
          setIsOpen(true);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < itemsWithAddNew.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (focusedIndex >= 0 && itemsWithAddNew[focusedIndex]) {
            handleItemSelect(itemsWithAddNew[focusedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
      }
    };

    // Update items with onClick handlers that call handleItemSelect
    // Always wrap onClick to ensure search value is updated with label
    const itemsWithHandlers = itemsWithAddNew.map((item) => {
      // Check if this is the "Add New" item (its onClick already handles selection properly)
      const isAddNewItem = showAddNew && item.label?.startsWith("+ Add");

      return {
        ...item,
        onClick: () => {
          // Call the custom onClick if it exists (for Add New items, etc.)
          item.onClick?.();
          // Call handleItemSelect to update search value with label and close dropdown
          // Skip for "Add New" items since their onClick already handles everything
          if (!isAddNewItem) {
            handleItemSelect(item);
          }
        },
      };
    });

    const showDropdown = isOpen && searchValue.length >= minSearchLength;

    // Render dropdown menu content
    const renderDropdownContent = () => (
      <DropdownMenu
        items={itemsWithHandlers}
        sectionHeading={sectionHeading}
        isLoading={isLoading}
        isEmpty={itemsWithAddNew.length === 0 && !showAddNew}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyLinkText={emptyLinkText}
        onEmptyLinkClick={onEmptyLinkClick}
        primaryButtonText={primaryButtonText}
        secondaryButtonText={secondaryButtonText}
        onPrimaryClick={onPrimaryClick}
        onSecondaryClick={onSecondaryClick}
        showChevron={showChevron}
        emptyIcon={emptyIcon}
        disableFooter={disableFooter}
        showFooter={
          (primaryButtonText || secondaryButtonText) && !disableFooter
            ? true
            : false
        }
        footerLayout={footerLayout}
        onClose={() => setIsOpen(false)}
        focusedIndex={focusedIndex}
        className={dropdownClassName}
        width={isMobile ? "full" : (dropdownWidth === "full" ? "full" : "auto")}
        maxHeight={`${position.maxHeight}px`}
        unstyled={isMobile}
      />
    );

    // Mobile: BottomSheet, Desktop: Regular Dropdown
    const dropdownMenu = showDropdown && (
      isMobile ? (
        <BottomSheet
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={sectionHeading}
          variant="default"
          showDragHandle={true}
          closeOnOverlayClick={true}
          closeOnEscape={true}
          closeOnSwipeDown={true}
        >
          {/* Search TextField inside BottomSheet for mobile */}
          <div className="mb-4">
            <TextField
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              containerClassName="mb-0"
              placeholder={textFieldProps.placeholder || "Search..."}
              {...textFieldProps}
            />
          </div>
          {renderDropdownContent()}
        </BottomSheet>
      ) : (
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            ...(position.top !== undefined && { top: `${position.top}px` }),
            ...(position.bottom !== undefined && {
              bottom: `${position.bottom}px`,
            }),
            left: `${position.left}px`,
            width: dropdownWidth === "full" ? `${position.width}px` : "auto",
            zIndex: isInsideModal ? 10001 : 9999,
          }}
        >
          {renderDropdownContent()}
        </div>
      )
    );

    return (
      <>
        <div ref={dropdownRef} className={cn("relative", containerClassName)}>
          {/* Show TextField only on desktop, on mobile it's inside BottomSheet */}
          {!isMobile && (
            <TextField
              ref={inputRef}
              value={searchValue}
              onChange={handleSearchChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              containerClassName="mb-0"
              {...textFieldProps}
            />
          )}

          {/* On mobile, show a trigger that opens the BottomSheet */}
          {isMobile && (
            <TextField
              ref={inputRef}
              value={searchValue}
              onFocus={() => {
                if (showOnFocus && searchValue.length >= minSearchLength) {
                  setIsOpen(true);
                }
              }}
              onClick={() => setIsOpen(true)}
              readOnly
              containerClassName="mb-0"
              {...textFieldProps}
            />
          )}
        </div>

        {/* Dropdown Menu Portal */}
        {typeof document !== "undefined" &&
          dropdownMenu &&
          createPortal(dropdownMenu, document.body)}
      </>
    );
  }
);

SearchableDropdown.displayName = "SearchableDropdown";
